import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  X, Edit, Save, Plus, Trash2, CheckCircle, XCircle,
  ExternalLink, Mail, Building2, Camera, Video,
  Shield, Eye, EyeOff, Copy,
} from "lucide-react";
import { userStatus, badgeStyle } from "@/utils/szStatus";

const GRAD = "linear-gradient(120deg,#572B7C,#78287C)";

const UserProfileModal = ({ user, isOpen, onClose, onUpdate, onDelete }) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [showSensitiveData, setShowSensitiveData] = useState(false);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);

  // Initialize form data when user changes
  useEffect(() => {
    if (user) {
      setFormData({
        // Identity
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        full_name: user.full_name || '',
        email: user.email || '',
        phone: user.phone || '',

        // Business
        business_name: user.business_name || '',
        abn: user.abn || '',
        gst_registered: user.gst_registered || false,

        // Location
        location: user.location || '',
        address: user.address || '',

        // Profile
        bio: user.bio || '',
        role: user.role || '',
        skills: user.skills || [],
        equipment_list: user.equipment_list || [],

        // Links
        website_url: user.website_url || '',
        facebook_url: user.facebook_url || '',
        instagram_url: user.instagram_url || '',
        portfolio_links: user.portfolio_links || [],
        video_links: user.video_links || [],
        editing_links: user.editing_links || [],

        // Certifications
        casa_repl: user.casa_repl || false,
        casa_reoc: user.casa_reoc || false,
        wwcc: user.wwcc || false,

        // Banking
        banking_details: user.banking_details || {},

        // Status
        approval_status: user.approval_status || 'pending',
        account_status: user.account_status || 'active',
        profile_submitted: user.profile_submitted || false,

        // Notification settings
        notification_preferences: user.notification_preferences || {},
        job_location_radius: user.job_location_radius || 50
      });
      setActiveTab('overview');
      setIsEditMode(false);
    }
  }, [user]);

  if (!isOpen || !user) return null;

  // Computed values
  const displayName = formData.full_name || `${formData.first_name || ''} ${formData.last_name || ''}`.trim() || 'User';
  const initials = displayName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'U';
  const userPhotoUrl = user.profile_photo_url;

  // Skill checks
  const hasSkill = (skill) => formData.skills?.includes(skill);
  const isPhotographer = hasSkill('photographer');
  const isVideographer = hasSkill('videographer');
  const isEditor = hasSkill('editor_photos') || hasSkill('editor_video');
  const isDroneOperator = hasSkill('drone_operator');

  // Format date helper
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Copy to clipboard helper
  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  // Mask sensitive data
  const maskData = (data, showFull = false) => {
    if (!data) return 'N/A';
    if (showFull || showSensitiveData) return data;
    if (data.length <= 4) return '****';
    return '****' + data.slice(-4);
  };

  // Handle save
  const handleSave = async () => {
    setLoading(true);
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/users/${user.id}`,
        formData
      );
      toast.success('Profile updated successfully');
      setIsEditMode(false);
      onUpdate();
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!window.confirm(`Are you sure you want to permanently delete ${displayName}?\n\nThis action cannot be undone.`)) {
      return;
    }
    setLoading(true);
    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${user.id}/delete`);
      toast.success('User deleted successfully');
      onDelete();
      onClose();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Failed to delete user');
    } finally {
      setLoading(false);
    }
  };

  // Handle password reset
  const handleSendPasswordReset = async () => {
    if (!window.confirm(`Send password reset email to ${user.email}?`)) return;
    setLoading(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/admin/trigger-password-reset`, {
        user_email: user.email
      });
      toast.success('Password reset email sent successfully');
    } catch (error) {
      console.error('Error sending password reset:', error);
      toast.error(error.response?.data?.detail || 'Failed to send password reset email');
    } finally {
      setLoading(false);
    }
  };

  // Handle status change
  const handleStatusChange = async (newStatus) => {
    setLoading(true);
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${user.id}/approval`,
        { approval_status: newStatus }
      );
      toast.success(`User status updated to ${newStatus}`);
      onUpdate();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    } finally {
      setLoading(false);
    }
  };

  // Handle link changes in edit mode
  const handleLinkChange = (field, index, value) => {
    const newLinks = [...(formData[field] || [])];
    newLinks[index] = value;
    setFormData(prev => ({ ...prev, [field]: newLinks }));
  };

  const handleAddLink = (field) => {
    setFormData(prev => ({ ...prev, [field]: [...(prev[field] || []), ''] }));
  };

  const handleRemoveLink = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: (prev[field] || []).filter((_, i) => i !== index)
    }));
  };

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'business', label: 'Business' },
    { id: 'portfolio', label: 'Portfolio' },
    { id: 'banking', label: 'Banking' },
    { id: 'system', label: 'System' },
  ];

  // 2-col label/value field
  const Field = ({ label, value, copyable = false, span = false, children }) => (
    <div style={span ? { gridColumn: "1 / -1" } : undefined}>
      <div className="sz-overline text-[#8A90A2] mb-1 flex items-center gap-1.5">
        {label}
        {copyable && value && value !== 'N/A' && (
          <button onClick={() => copyToClipboard(value, label)} className="text-[#A8AEBC] hover:text-[#0C1120]" title="Copy">
            <Copy className="w-3 h-3" />
          </button>
        )}
      </div>
      {children ?? <div className="text-[13.5px] text-[#0C1120] font-medium break-words">{value || 'N/A'}</div>}
    </div>
  );

  const stCfg = userStatus(formData.approval_status);

  // Build the read-only field set for the active tab
  const renderViewTab = () => {
    if (activeTab === 'overview') {
      return (
        <>
          <Field label="Full Name" value={displayName} />
          <Field label="Email" value={user.email} copyable />
          <Field label="Phone" value={user.phone} copyable />
          <Field label="Location" value={formData.address || formData.location} />
          <Field label="Role">
            <span className="sz-badge sz-nodot" style={{ color: "#572B7C", background: "#EDE4F4" }}>{formData.role || 'Not Set'}</span>
          </Field>
          <Field label="Approval Status">
            <span className="sz-badge" style={badgeStyle(stCfg)}>{stCfg.label}</span>
          </Field>
          <Field label="Account Status" value={formData.account_status} />
          <Field label="Member Since" value={formatDate(user.created_at)} />
          {user.approved_at && <Field label="Approved At" value={formatDate(user.approved_at)} />}
          {user.last_login && <Field label="Last Login" value={formatDate(user.last_login)} />}
          <Field label="WWCC" value={formData.wwcc ? 'Verified' : 'Not Provided'} />
          {isDroneOperator && <Field label="CASA RePL" value={formData.casa_repl ? 'Yes' : 'No'} />}
          {isDroneOperator && <Field label="CASA ReOC" value={formData.casa_reoc ? 'Yes' : 'No'} />}
          <Field label="Skills" span>
            <div className="flex flex-wrap gap-2">
              {formData.skills?.length > 0
                ? formData.skills.map((s) => (
                    <span key={s} className="sz-badge sz-nodot" style={{ color: "#0F766E", background: "#CCFBF1" }}>{s.replace(/_/g, ' ')}</span>
                  ))
                : <span className="text-[#A8AEBC] text-sm">No skills added</span>}
            </div>
          </Field>
          <Field label="Bio" span>
            <p className="text-[13.5px] text-[#2B3142] whitespace-pre-wrap font-normal">{formData.bio || 'No bio provided'}</p>
          </Field>
        </>
      );
    }
    if (activeTab === 'business') {
      return (
        <>
          <Field label="Business Name" value={formData.business_name} />
          <Field label="ABN" value={formData.abn} copyable />
          <Field label="GST Registered" value={formData.gst_registered ? 'Yes' : 'No'} />
          <Field label="Equipment" span>
            <div className="flex flex-col gap-2">
              {formData.equipment_list?.length > 0
                ? formData.equipment_list.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2 px-3 py-2 bg-[#F5F6F9] rounded-lg">
                      <Camera className="w-4 h-4 text-[#8A90A2]" />
                      <span className="text-sm text-[#2B3142]">{item}</span>
                    </div>
                  ))
                : <p className="text-sm text-[#A8AEBC]">No equipment listed</p>}
            </div>
          </Field>
        </>
      );
    }
    if (activeTab === 'portfolio') {
      const Link = ({ href, children }) => (
        <a href={href.startsWith('http') ? href : `https://${href}`} target="_blank" rel="noopener noreferrer"
          className="text-[13.5px] text-[#2563EB] hover:underline inline-flex items-center gap-1 break-all">
          {children}<ExternalLink className="w-3 h-3 shrink-0" />
        </a>
      );
      return (
        <>
          <Field label="Website">
            {formData.website_url ? <Link href={formData.website_url}>{formData.website_url}</Link> : <span className="text-[#A8AEBC] text-sm">N/A</span>}
          </Field>
          <Field label="Instagram">
            {formData.instagram_url
              ? <a href={formData.instagram_url.startsWith('http') ? formData.instagram_url : `https://instagram.com/${formData.instagram_url}`} target="_blank" rel="noopener noreferrer" className="text-[13.5px] text-[#2563EB] hover:underline inline-flex items-center gap-1 break-all">{formData.instagram_url}<ExternalLink className="w-3 h-3 shrink-0" /></a>
              : <span className="text-[#A8AEBC] text-sm">N/A</span>}
          </Field>
          <Field label="Facebook">
            {formData.facebook_url
              ? <a href={formData.facebook_url.startsWith('http') ? formData.facebook_url : `https://facebook.com/${formData.facebook_url}`} target="_blank" rel="noopener noreferrer" className="text-[13.5px] text-[#2563EB] hover:underline inline-flex items-center gap-1 break-all">{formData.facebook_url}<ExternalLink className="w-3 h-3 shrink-0" /></a>
              : <span className="text-[#A8AEBC] text-sm">N/A</span>}
          </Field>
          {isPhotographer && (
            <Field label="Photo Portfolio" span>
              <div className="flex flex-col gap-2">
                {formData.portfolio_links?.filter(l => l).length > 0
                  ? formData.portfolio_links.filter(l => l).map((link, idx) => (
                      <a key={idx} href={link} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-3 py-2 bg-[#EDE4F4] text-[#572B7C] rounded-lg text-sm">
                        <Camera className="w-4 h-4" /><span className="truncate flex-1">{link}</span><ExternalLink className="w-4 h-4" />
                      </a>
                    ))
                  : <p className="text-sm text-[#A8AEBC]">No portfolio links</p>}
              </div>
            </Field>
          )}
          {isVideographer && (
            <Field label="Video Showcase" span>
              <div className="flex flex-col gap-2">
                {formData.video_links?.filter(l => l).length > 0
                  ? formData.video_links.filter(l => l).map((link, idx) => (
                      <a key={idx} href={link} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-3 py-2 bg-[#DBEAFE] text-[#2563EB] rounded-lg text-sm">
                        <Video className="w-4 h-4" /><span className="truncate flex-1">{link}</span><ExternalLink className="w-4 h-4" />
                      </a>
                    ))
                  : <p className="text-sm text-[#A8AEBC]">No video links</p>}
              </div>
            </Field>
          )}
          {isEditor && (
            <Field label="Editing Portfolio" span>
              <div className="flex flex-col gap-2">
                {formData.editing_links?.filter(l => l).length > 0
                  ? formData.editing_links.filter(l => l).map((link, idx) => (
                      <a key={idx} href={link} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-3 py-2 bg-[#DCFCE7] text-[#15803D] rounded-lg text-sm">
                        <span className="truncate flex-1">{link}</span><ExternalLink className="w-4 h-4" />
                      </a>
                    ))
                  : <p className="text-sm text-[#A8AEBC]">No editing links</p>}
              </div>
            </Field>
          )}
        </>
      );
    }
    if (activeTab === 'banking') {
      return (
        <>
          <div className="col-span-full flex items-center justify-end -mb-2">
            <button onClick={() => setShowSensitiveData(!showSensitiveData)} className="flex items-center gap-1 text-sm text-[#572B7C] hover:text-[#78287C]">
              {showSensitiveData ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              {showSensitiveData ? 'Hide' : 'Show'} sensitive data
            </button>
          </div>
          <Field label="Account Name" value={formData.banking_details?.account_name || 'N/A'} />
          <Field label="BSB" value={maskData(formData.banking_details?.bsb)} copyable={showSensitiveData} />
          <Field label="Account Number" value={maskData(formData.banking_details?.account_number)} copyable={showSensitiveData} />
          <Field label="Bank Name" value={formData.banking_details?.bank_name || 'N/A'} />
          <Field label="Stripe Customer" value={user.stripe_customer_id || 'Not Connected'} copyable={!!user.stripe_customer_id} />
          <Field label="Stripe Account" value={user.stripe_account_id || 'Not Connected'} copyable={!!user.stripe_account_id} />
        </>
      );
    }
    // system
    return (
      <>
        <Field label="User ID" value={user.id} copyable />
        <Field label="User Number" value={user.user_number} copyable />
        {user.mysql_user_id && <Field label="MySQL User ID" value={user.mysql_user_id} copyable />}
        <Field label="Auth Provider" value={user.auth_provider || 'Email/Password'} />
        {user.google_id && <Field label="Google ID" value={user.google_id} copyable />}
        {user.apple_id && <Field label="Apple ID" value={user.apple_id} copyable />}
        <Field label="Email Verified" value={user.email_verified ? 'Verified' : 'Not Verified'} />
        <Field label="Push Token" value={user.push_token ? 'Registered' : 'Not Registered'} />
        {user.push_platform && <Field label="Platform" value={user.push_platform} />}
        <Field label="Notification Radius" value={`${formData.job_location_radius || 50} km`} />
        <Field label="Created" value={formatDate(user.created_at)} />
        <Field label="Last Updated" value={formatDate(user.updated_at)} />
        <Field label="Last Login" value={formatDate(user.last_login)} />
        {user.approved_at && <Field label="Approved" value={formatDate(user.approved_at)} />}
        {user.email_bounced_timestamp && <Field label="Email Bounced" value={formatDate(user.email_bounced_timestamp)} />}
      </>
    );
  };

  return (
    <div className="sz-modal-backdrop sz-fade" onClick={onClose}>
      <div className="sz-modal-card w-full max-w-[680px]" onClick={(e) => e.stopPropagation()}>
        {/* Gradient header */}
        <div className="px-6 py-[22px] text-white" style={{ background: GRAD }}>
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3.5 min-w-0">
              <div className="w-[54px] h-[54px] rounded-full border-2 border-white/30 bg-white/15 flex items-center justify-center font-bold text-lg shrink-0 overflow-hidden" style={{ fontFamily: "Poppins" }}>
                {userPhotoUrl ? <img src={userPhotoUrl} alt="" className="w-full h-full object-cover" /> : initials}
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2.5 flex-wrap">
                  <h3 className="text-[19px] font-bold m-0 truncate" style={{ fontFamily: "Poppins" }}>{displayName}</h3>
                  {user.user_number && (
                    <span className="text-[11px] font-bold bg-white/20 px-2.5 py-[3px] rounded-full" style={{ fontFamily: "Poppins" }}>USR-{user.user_number}</span>
                  )}
                </div>
                <div className="text-[12.5px] text-white/80 mt-[3px] truncate">{user.email}</div>
              </div>
            </div>
            <button onClick={onClose} className="w-8 h-8 rounded-[9px] bg-white/15 hover:bg-white/25 text-white flex items-center justify-center shrink-0">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="flex gap-2 mt-4 flex-wrap">
            <span className="text-[11px] font-bold px-2.5 py-1 rounded-full" style={{ ...badgeStyle(stCfg), fontFamily: "Poppins" }}>{stCfg.label}</span>
            <span className="text-[11px] font-semibold bg-white/15 px-2.5 py-1 rounded-full">{formData.role || 'crew'}</span>
            {user.email_bounced && <span className="text-[11px] font-semibold bg-[#FFEDD5] text-[#EA580C] px-2.5 py-1 rounded-full">Email Bounced</span>}
            {!user.profile_submitted && <span className="text-[11px] font-semibold bg-white/15 px-2.5 py-1 rounded-full">Profile Incomplete</span>}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-0.5 px-4 border-b border-[#EEF0F4] bg-white overflow-x-auto szscroll">
          {tabs.map((tab) => {
            const on = activeTab === tab.id;
            return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className="px-3.5 py-3.5 text-[13px] font-semibold whitespace-nowrap"
                style={{ color: on ? "#572B7C" : "#8A90A2", borderBottom: `2px solid ${on ? "#572B7C" : "transparent"}` }}>
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto szscroll px-6 py-5">
          {!isEditMode ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-[22px] gap-y-4">
              {renderViewTab()}
            </div>
          ) : (
            // EDIT MODE — 2-col form
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
              <div>
                <Label className="text-xs font-semibold text-[#2B3142]">First Name</Label>
                <Input value={formData.first_name} onChange={(e) => setFormData(p => ({ ...p, first_name: e.target.value }))} className="mt-1.5" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-[#2B3142]">Last Name</Label>
                <Input value={formData.last_name} onChange={(e) => setFormData(p => ({ ...p, last_name: e.target.value }))} className="mt-1.5" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-[#2B3142]">Email Address</Label>
                <Input type="email" value={formData.email} onChange={(e) => setFormData(p => ({ ...p, email: e.target.value }))} className="mt-1.5" placeholder="user@example.com" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-[#2B3142]">Phone</Label>
                <Input value={formData.phone} onChange={(e) => setFormData(p => ({ ...p, phone: e.target.value }))} className="mt-1.5" />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs font-semibold text-[#2B3142]">Location</Label>
                <Input value={formData.address || formData.location} onChange={(e) => setFormData(p => ({ ...p, address: e.target.value }))} className="mt-1.5" placeholder="e.g., Sydney, NSW" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-[#2B3142]">Business Name</Label>
                <Input value={formData.business_name} onChange={(e) => setFormData(p => ({ ...p, business_name: e.target.value }))} className="mt-1.5" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-[#2B3142]">ABN</Label>
                <Input value={formData.abn} onChange={(e) => setFormData(p => ({ ...p, abn: e.target.value }))} className="mt-1.5" />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs font-semibold text-[#2B3142]">GST Registered</Label>
                <div className="flex items-center gap-4 mt-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" checked={formData.gst_registered === true} onChange={() => setFormData(p => ({ ...p, gst_registered: true }))} className="accent-[#572B7C]" />
                    <span className="text-sm">Yes</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" checked={formData.gst_registered === false} onChange={() => setFormData(p => ({ ...p, gst_registered: false }))} className="accent-[#572B7C]" />
                    <span className="text-sm">No</span>
                  </label>
                </div>
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs font-semibold text-[#2B3142]">Bio</Label>
                <Textarea value={formData.bio} onChange={(e) => setFormData(p => ({ ...p, bio: e.target.value }))} placeholder="Write a bio..." rows={4} className="mt-1.5" />
              </div>

              {isPhotographer && (
                <div className="sm:col-span-2">
                  <Label className="text-xs font-semibold text-[#2B3142]">Photo Portfolio Links</Label>
                  <div className="flex flex-col gap-2 mt-1.5">
                    {(formData.portfolio_links || []).map((link, idx) => (
                      <div key={idx} className="flex gap-2">
                        <Input value={link} onChange={(e) => handleLinkChange('portfolio_links', idx, e.target.value)} placeholder="https://..." />
                        <Button type="button" variant="outline" size="icon" onClick={() => handleRemoveLink('portfolio_links', idx)} className="border-red-300 text-red-500 hover:bg-red-50"><X className="w-4 h-4" /></Button>
                      </div>
                    ))}
                    <Button type="button" variant="outline" onClick={() => handleAddLink('portfolio_links')} className="w-full"><Plus className="w-4 h-4 mr-2" />Add Link</Button>
                  </div>
                </div>
              )}

              {isVideographer && (
                <div className="sm:col-span-2">
                  <Label className="text-xs font-semibold text-[#2B3142]">Video Links</Label>
                  <div className="flex flex-col gap-2 mt-1.5">
                    {(formData.video_links || []).map((link, idx) => (
                      <div key={idx} className="flex gap-2">
                        <Input value={link} onChange={(e) => handleLinkChange('video_links', idx, e.target.value)} placeholder="https://..." />
                        <Button type="button" variant="outline" size="icon" onClick={() => handleRemoveLink('video_links', idx)} className="border-red-300 text-red-500 hover:bg-red-50"><X className="w-4 h-4" /></Button>
                      </div>
                    ))}
                    <Button type="button" variant="outline" onClick={() => handleAddLink('video_links')} className="w-full"><Plus className="w-4 h-4 mr-2" />Add Link</Button>
                  </div>
                </div>
              )}

              {isDroneOperator && (
                <div className="sm:col-span-2 flex flex-col gap-3">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input type="checkbox" checked={formData.casa_repl} onChange={(e) => setFormData(p => ({ ...p, casa_repl: e.target.checked }))} className="w-5 h-5 accent-[#572B7C] rounded" />
                    <span className="text-sm font-medium">CASA RePL Certified</span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input type="checkbox" checked={formData.casa_reoc} onChange={(e) => setFormData(p => ({ ...p, casa_reoc: e.target.checked }))} className="w-5 h-5 accent-[#572B7C] rounded" />
                    <span className="text-sm font-medium">CASA ReOC Certified</span>
                  </label>
                </div>
              )}

              <div className="sm:col-span-2">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" checked={formData.wwcc} onChange={(e) => setFormData(p => ({ ...p, wwcc: e.target.checked }))} className="w-5 h-5 accent-[#572B7C] rounded" />
                  <span className="text-sm font-medium">WWCC Verified</span>
                </label>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-3.5 border-t border-[#EEF0F4]">
          <div className="flex flex-wrap items-center gap-2">
            {!isEditMode && (
              <>
                {formData.approval_status === 'pending' && (
                  <>
                    <Button onClick={() => handleStatusChange('approved')} disabled={loading} size="sm" className="bg-[#15803D] hover:bg-[#136a33] text-white">
                      <CheckCircle className="w-4 h-4 mr-1" />Approve
                    </Button>
                    <Button onClick={() => handleStatusChange('rejected')} disabled={loading} variant="outline" size="sm" className="border-red-500 text-red-500 hover:bg-red-50">
                      <XCircle className="w-4 h-4 mr-1" />Reject
                    </Button>
                  </>
                )}
                {formData.approval_status === 'approved' && (
                  <Button onClick={() => handleStatusChange('suspended')} disabled={loading} variant="outline" size="sm" className="border-gray-400 text-gray-600 hover:bg-gray-50">
                    <Shield className="w-4 h-4 mr-1" />Suspend
                  </Button>
                )}
                {(formData.approval_status === 'rejected' || formData.approval_status === 'suspended') && (
                  <Button onClick={() => handleStatusChange('approved')} disabled={loading} size="sm" className="bg-[#15803D] hover:bg-[#136a33] text-white">
                    <CheckCircle className="w-4 h-4 mr-1" />Reactivate
                  </Button>
                )}
                <Button onClick={handleSendPasswordReset} disabled={loading} variant="outline" size="sm" className="border-[#572B7C] text-[#572B7C] hover:bg-[#EDE4F4]">
                  <Mail className="w-4 h-4 mr-1" />Reset Password
                </Button>
                <Button onClick={handleDelete} disabled={loading} variant="outline" size="sm" className="border-red-500 text-red-500 hover:bg-red-50">
                  <Trash2 className="w-4 h-4 mr-1" />Delete
                </Button>
              </>
            )}
          </div>
          <div className="flex items-center gap-2.5">
            {!isEditMode ? (
              <>
                <button onClick={onClose} className="sz-btn sz-btn-outline">Close</button>
                <button onClick={() => setIsEditMode(true)} className="sz-btn sz-btn-primary">
                  <Edit className="w-[15px] h-[15px]" />Edit Profile
                </button>
              </>
            ) : (
              <>
                <button onClick={() => setIsEditMode(false)} disabled={loading} className="sz-btn sz-btn-outline">Cancel</button>
                <button onClick={handleSave} disabled={loading} className="sz-btn sz-btn-primary">
                  <Save className="w-[15px] h-[15px] mr-0.5" />{loading ? 'Saving...' : 'Save Changes'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfileModal;
